import { useState } from "react";
import { useAdminHomepageBanners, HomepageBanner } from "@/hooks/useHomepageBanners";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, GripVertical } from "lucide-react";
import HomepageBannerForm from "./HomepageBannerForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type BannerStatus = {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  className?: string;
};

const getStatus = (banner: HomepageBanner): BannerStatus => {
  const now = new Date();
  const start = banner.start_date ? new Date(banner.start_date) : null;
  const end = banner.end_date ? new Date(banner.end_date) : null;

  if (!banner.is_active) return { label: "Disabled", variant: "secondary", className: "bg-muted text-muted-foreground" };
  if (start && now < start) return { label: "Scheduled", variant: "outline", className: "border-warning text-warning" };
  if (end && now > end) return { label: "Expired", variant: "outline", className: "text-muted-foreground" };
  return { label: "Active", variant: "default", className: "bg-success text-success-foreground" };
};

const HomepageBannersList = () => {
  const { banners, isLoading, deleteBanner, toggleBanner } = useAdminHomepageBanners();
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<HomepageBanner | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleEdit = (banner: HomepageBanner) => {
    setEditingBanner(banner);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (deletingId) {
      await deleteBanner.mutateAsync(deletingId);
      setDeletingId(null);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingBanner(null);
  };

  const handleToggle = (banner: HomepageBanner) => {
    toggleBanner.mutate({ id: banner.id, is_active: !banner.is_active });
  };

  if (showForm) {
    return (
      <HomepageBannerForm
        banner={editingBanner}
        onClose={handleCloseForm}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Homepage Banners</h2>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Banner
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : banners.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">No homepage banners yet</p>
          <Button onClick={() => setShowForm(true)}>Create First Banner</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {banners.map((banner) => {
            const status = getStatus(banner);
            return (
              <Card key={banner.id} className="p-4">
                <div className="flex items-center gap-4">
                  <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab" />
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{banner.name}</h3>
                      <Badge 
                        variant={status.variant}
                        className={status.className}
                      >
                        {status.label}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {banner.style_variant}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {banner.title} • {banner.cta_link || 'No link'}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      checked={banner.is_active}
                      onCheckedChange={() => handleToggle(banner)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(banner)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingId(banner.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Banner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this homepage banner.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HomepageBannersList;
